import { useCallback, useState, useEffect } from 'react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Header } from '@/components/layout/Header'
import { ToolboxPanel } from '@/components/panels/ToolboxPanel'
import { PropertiesPanel } from '@/components/panels/PropertiesPanel'
import { LabelCanvas } from '@/components/canvas/LabelCanvas'
import { ZPLPreviewPanel } from '@/components/panels/ZPLPreviewPanel'
import { NewLabelDialog } from '@/components/dialogs/NewLabelDialog'
import { DesignsDialog } from '@/components/dialogs/DesignsDialog'
import { SettingsDialog } from '@/components/dialogs/SettingsDialog'
import { ImportZPLDialog } from '@/components/dialogs/ImportZPLDialog'
import { AboutDialog } from '@/components/dialogs/AboutDialog'
import { useElementsStore } from '@/stores/elementsStore'
import { useLabelStore } from '@/stores/labelStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { generateZPL } from '@/lib/zpl/generator'
import { generateId } from '@/lib/utils/generateId'
import {
  ElementType,
  TextElement,
  BarcodeElement,
  QRCodeElement,
  DataMatrixElement,
  GS1DataMatrixElement,
  GS1Code128Element,
  LineElement,
  RectangleElement,
  CircleElement,
  ImageElement,
  LabelElement,
} from '@/types'

function App() {
  const [newLabelOpen, setNewLabelOpen] = useState(false)
  const [designsOpen, setDesignsOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [importZPLOpen, setImportZPLOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isElectron, setIsElectron] = useState(false)

  const { addElement, clearElements } = useElementsStore()
  const { label } = useLabelStore()
  const { variableData, lastClickPosition, setLastClickPosition, printer } = useSettingsStore()
  const { elements } = useElementsStore()

  // Check if running in Electron
  useEffect(() => {
    const checkElectron = async () => {
      if (window.electronAPI) {
        try {
          const result = await window.electronAPI.isElectron()
          setIsElectron(result)
        } catch {
          setIsElectron(false)
        }
      }
    }
    checkElectron()
  }, [])

  const handleAddElement = useCallback(
    (type: ElementType) => {
      const id = generateId()
      // Use last click position, offset slightly down for each new element
      const basePosition = {
        x: Math.min(lastClickPosition.x, label.width - 5),
        y: Math.min(lastClickPosition.y, label.height - 5)
      }
      // Update position for next element (offset down by 5mm)
      setLastClickPosition(
        lastClickPosition.x,
        Math.min(lastClickPosition.y + 5, label.height - 5)
      )

      let element: LabelElement

      switch (type) {
        case 'text':
          element = {
            id,
            type: 'text',
            position: basePosition,
            rotation: 'N',
            content: 'Sample Text',
            fontSize: 30,
            font: '0',
            isVariable: false,
          } as TextElement
          break

        case 'barcode-code128':
        case 'barcode-code39':
        case 'barcode-ean13':
        case 'barcode-ean8':
        case 'barcode-upca':
        case 'barcode-upce':
        case 'barcode-itf':
          element = {
            id,
            type,
            position: basePosition,
            rotation: 'N',
            content: type === 'barcode-ean13' ? '5901234123457' :
                     type === 'barcode-ean8' ? '96385074' :
                     type === 'barcode-upca' ? '012345678905' :
                     type === 'barcode-upce' ? '0123456' :
                     type === 'barcode-itf' ? '1234567890' :
                     'ABC-123',
            height: 5,
            moduleWidth: 2,
            showText: true,
            isVariable: false,
          } as BarcodeElement
          break

        case 'qrcode':
          element = {
            id,
            type: 'qrcode',
            position: basePosition,
            rotation: 'N',
            content: 'https://example.com',
            magnification: 4,
            errorCorrection: 'M',
            isVariable: false,
          } as QRCodeElement
          break

        case 'datamatrix':
          element = {
            id,
            type: 'datamatrix',
            position: basePosition,
            rotation: 'N',
            content: 'DMDATA123',
            size: 4,
            isVariable: false,
          } as DataMatrixElement
          break

        case 'gs1-datamatrix':
          element = {
            id,
            type: 'gs1-datamatrix',
            position: basePosition,
            rotation: 'N',
            applicationIdentifiers: [
              { ai: '01', value: '07891234567890' },
              { ai: '17', value: '251231' },
              { ai: '10', value: 'ABC123' },
            ],
            size: 4,
          } as GS1DataMatrixElement
          break

        case 'gs1-code128':
          element = {
            id,
            type: 'gs1-code128',
            position: basePosition,
            rotation: 'N',
            applicationIdentifiers: [
              { ai: '01', value: '07891234567890' },
              { ai: '17', value: '251231' },
              { ai: '10', value: 'ABC123' },
            ],
            height: 5,
            moduleWidth: 2,
            showText: true,
          } as GS1Code128Element
          break

        case 'line':
          element = {
            id,
            type: 'line',
            position: basePosition,
            rotation: 'N',
            width: 30,
            height: 0.5,
            color: 'B',
          } as LineElement
          break

        case 'rectangle':
          element = {
            id,
            type: 'rectangle',
            position: basePosition,
            rotation: 'N',
            width: 20,
            height: 15,
            borderThickness: 2,
            color: 'B',
            filled: false,
          } as RectangleElement
          break

        case 'circle':
          element = {
            id,
            type: 'circle',
            position: basePosition,
            rotation: 'N',
            diameter: 15,
            borderThickness: 2,
            color: 'B',
          } as CircleElement
          break

        default:
          return
      }

      addElement(element)
    },
    [addElement, lastClickPosition, label.width, label.height, setLastClickPosition]
  )

  const handleExportZPL = useCallback(() => {
    const zplCode = generateZPL(label, elements, variableData)
    const blob = new Blob([zplCode], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const fileName = label.name.replace(/\s+/g, '_') + '.zpl'
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [label, elements, variableData])

  const handleOpenDesigns = useCallback(() => {
    setDesignsOpen(true)
  }, [])

  const handleAddImage = useCallback(
    (imageData: Omit<ImageElement, 'id' | 'position' | 'rotation'>) => {
      const position = {
        x: Math.min(lastClickPosition.x, label.width - 5),
        y: Math.min(lastClickPosition.y, label.height - 5)
      }
      // Update position for next element (offset down by 5mm)
      setLastClickPosition(
        lastClickPosition.x,
        Math.min(lastClickPosition.y + 5, label.height - 5)
      )
      const element: ImageElement = {
        id: generateId(),
        position,
        rotation: 'N',
        ...imageData,
      }
      addElement(element)
    },
    [addElement, lastClickPosition, label.width, label.height, setLastClickPosition]
  )

  const handleDiscard = useCallback(() => {
    if (elements.length > 0 && window.confirm('Discard all elements from the label?')) {
      clearElements()
    }
  }, [elements.length, clearElements])

  const handleDirectPrint = useCallback(async () => {
    if (isPrinting) return

    setIsPrinting(true)
    const zplCode = generateZPL(label, elements, variableData)

    try {
      // Check connection type from settings
      const useHttp = printer.connectionType === 'http'
      const useHttps = printer.connectionType === 'https'

      if (!useHttp && !useHttps && isElectron && window.electronAPI) {
        // Use Electron direct TCP printing (only for TCP mode in Electron)
        const result = await window.electronAPI.printZPL({
          host: printer.ip,
          port: printer.port,
          zplCode,
        })

        if (!result.success) {
          throw new Error(result.message || 'Print failed')
        }
      } else if (useHttp || useHttps) {
        // Use HTTP/HTTPS via NEO ZPL Simulator
        const protocol = useHttps ? 'https' : 'http'
        const defaultPort = useHttps ? 3443 : 3001
        const simulatorUrl = `${protocol}://${printer.ip || '127.0.0.1'}:${printer.port || defaultPort}/api/print`
        const response = await fetch(simulatorUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            zpl: zplCode,
          }),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Connection failed' }))
          throw new Error(error.message || error.error || 'Print failed')
        }

        const result = await response.json()
        if (!result.success) {
          throw new Error(result.error || 'Print failed')
        }
      } else {
        // TCP mode but not in Electron - show error
        throw new Error('TCP Direct mode requires the Electron desktop app. Use HTTPS (Simulator) mode for web browsers.')
      }
    } catch (error) {
      alert(`Print error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsPrinting(false)
    }
  }, [isPrinting, label, elements, variableData, isElectron, printer])

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col bg-background text-foreground">
        <Header
          onNewLabel={() => setNewLabelOpen(true)}
          onSave={handleOpenDesigns}
          onLoad={handleOpenDesigns}
          onImportZPL={() => setImportZPLOpen(true)}
          onExportZPL={handleExportZPL}
          onPrint={handleDirectPrint}
          onSettings={() => setSettingsOpen(true)}
          onDiscard={handleDiscard}
          onAbout={() => setAboutOpen(true)}
          isPrinting={isPrinting}
        />

        <div className="flex-1 flex overflow-hidden">
          <ToolboxPanel onAddElement={handleAddElement} onAddImage={handleAddImage} />

          <div className="flex-1 flex flex-col overflow-hidden">
            <LabelCanvas />
            <PropertiesPanel />
          </div>

          <ZPLPreviewPanel />
        </div>

        <NewLabelDialog open={newLabelOpen} onOpenChange={setNewLabelOpen} />
        <DesignsDialog open={designsOpen} onOpenChange={setDesignsOpen} />
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        <ImportZPLDialog open={importZPLOpen} onOpenChange={setImportZPLOpen} />
        <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      </div>
    </TooltipProvider>
  )
}

export default App
